import {useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {BeneficiaryModel} from 'domain/usecases/others/selection-flow/beneficiary-v3';
import {removeAuthTokens} from 'infra/http/utils';
import {HomeEnum} from 'presentation/constants';
import {useGetSidebarMutation} from 'presentation/mutation/useGetSidebarMutation';
import {parseJwt} from 'presentation/utils';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {useSetRecoilState} from 'recoil';
import {accountDataStateRefresher} from 'presentation/pages/states';
import {usePostBeneficiaryMutation} from '../mutation/usePostBeneficiaryMutation';
import {useIsSubmittingSelectionStore} from '../store/useIsSubmittingSelectionStore';
import {useSelectedBeneficiaryStore} from '../store/useSelectedBeneficiaryStore';

type ConfirmSelectionParams = {
	beneficiary?: BeneficiaryModel;
	beneficiariesCount?: number;
};

export function useSelection() {
	const setAccountData = useAccountDataStore(state => state.setAccountData);
	const resetAccountData = useAccountDataStore(state => state.resetAccountData);
	const selectedBeneficiary = useSelectedBeneficiaryStore(state => state.selectedBeneficiary);
	const isSubmittingSelection = useIsSubmittingSelectionStore(
		state => state.isSubmittingSelection,
	);
	const setIsSubmittingSelection = useIsSubmittingSelectionStore(
		state => state.setIsSubmittingSelection,
	);
	const navigate = useNavigate();
	const {state} = useLocation();
	const setAccountDataStateRefresher = useSetRecoilState(accountDataStateRefresher);

	const getAccessedUser = async (token: string) => {
		if (!token) {
			return;
		}

		const parsedToken = parseJwt(token);

		const {cdPessoa, cdUsuario} = parsedToken.data;

		setAccountData({
			accessed_user: {
				cdPessoa,
				cdUsuario,
			},
		});
	};

	const {mutateAsync: postBeneficiary} = usePostBeneficiaryMutation();
	const {mutateAsync: getSidebar} = useGetSidebarMutation();

	const confirmSelection = async ({beneficiary, beneficiariesCount}: ConfirmSelectionParams) => {
		const currentBeneficiary = beneficiary ?? selectedBeneficiary;
		if (isSubmittingSelection || !currentBeneficiary) return;
		const {nmPlano, nmUsuarioC, nuContrato, cdUsuarioC} = currentBeneficiary;
		setIsSubmittingSelection(true);
		try {
			const beneficiaryData = await postBeneficiary({
				nmPlano,
				nmUsuarioC,
				nuContrato,
				cdUsuarioC,
			});

			const sideBarData = await getSidebar();

			if (!sideBarData) {
				removeAuthTokens();
				resetAccountData();
				setAccountDataStateRefresher(val => val + 1);
			}

			getAccessedUser(beneficiaryData.access_token);

			setAccountData({
				beneficiary: sideBarData.beneficiary,
				sidebar: sideBarData.sideBar,
				accesses: sideBarData.accesses,
			});
			setAccountDataStateRefresher(val => val + 1);

			const isSingleBeneficiary = beneficiariesCount === 1;

			if (state && state.pathFrom && !isSingleBeneficiary) {
				navigate(state.pathFrom, {replace: true});
				return;
			}

			navigate(HomeEnum.HOME_PAGE, {
				state: {
					isCommingFromEntryFlow: true,
				},
			});
		} catch (error) {
			setIsSubmittingSelection(false);
			removeAuthTokens();
			resetAccountData();
			setAccountDataStateRefresher(val => val + 1);
		}
	};

	useEffect(
		() => () => {
			setIsSubmittingSelection(false);
		},
		[],
	);

	return {
		confirmSelection,
		isSubmitting: isSubmittingSelection,
	};
}
